import React from 'react';
import { Link } from 'react-router-dom';

function MarketingFeatures() {
  const featureCards = [
    {
      icon: '🌐',
      title: 'Professional Website',
      description: 'Custom domain, your branding, mobile-responsive design. Your customers see your brand, not ours.',
      highlights: ['Your own domain OR free subdomain', 'Your logo and colors', 'Mobile-friendly design', 'No website maintenance fees']
    },
    {
      icon: '🏠',
      title: 'Property Listings',
      description: 'Showcase your land inventory with professional listings that convert browsers to buyers.',
      highlights: ['Up to 10 photos per property', 'GPS maps and coordinates', 'Financing calculator', 'Property status tracking']
    },
    {
      icon: '💳',
      title: 'Payment Processing',
      description: 'Accept payments online with Square or Stripe. Secure, automatic, and professional.',
      highlights: ['Credit/debit cards', 'Square, Stripe, or custom processor', 'Automatic receipts', 'Payment reminders']
    },
    {
      icon: '👥',
      title: 'Customer Portal',
      description: 'Give your buyers 24/7 access to view balances, make payments, and download documents.',
      highlights: ['Secure customer login', 'View loan details', 'Make payments online', 'Download contracts']
    },
    {
      icon: '📄',
      title: 'Contracts & Documents',
      description: 'Auto-generate professional contracts. Store and share documents securely with buyers.',
      highlights: ['Contract templates', 'Auto-fill buyer info', 'Document storage', 'Secure sharing']
    },
    {
      icon: '📊',
      title: 'Loan Management',
      description: 'Track every loan from origination to payoff with automatic calculations and reminders.',
      highlights: ['Amortization schedules', 'Payment tracking', 'Late fee automation', 'Default management']
    }
  ];

  const comparisonFeatures = [
    { feature: 'Property Listings Website', spreadsheets: false, loanOnly: false, deedstack: true },
    { feature: 'Customer-Facing Portal', spreadsheets: false, loanOnly: 'Limited', deedstack: true },
    { feature: 'Online Payment Processing', spreadsheets: false, loanOnly: true, deedstack: true },
    { feature: 'Loan Amortization', spreadsheets: 'Manual', loanOnly: true, deedstack: true },
    { feature: 'Contract Generation', spreadsheets: false, loanOnly: false, deedstack: true },
    { feature: 'Custom Domain', spreadsheets: false, loanOnly: false, deedstack: true },
    { feature: 'White-Label Branding', spreadsheets: false, loanOnly: false, deedstack: true },
    { feature: 'eBay Integration', spreadsheets: false, loanOnly: false, deedstack: true },
    { feature: 'GPS Property Maps', spreadsheets: false, loanOnly: false, deedstack: true },
    { feature: 'Multi-State Support', spreadsheets: 'Manual', loanOnly: 'Limited', deedstack: true },
    { feature: 'Tax & HOA Tracking', spreadsheets: 'Manual', loanOnly: false, deedstack: true },
    { feature: 'Automated Reminders', spreadsheets: false, loanOnly: 'Limited', deedstack: true }
  ];

  const tiers = [
    {
      name: 'Starter',
      price: '$69',
      setup: null, // No setup fee
      description: 'Perfect for getting started',
      limits: ['25 active loans', '50 properties'],
      features: ['Customer portal', 'Payment processing', 'Custom domain', 'Logo upload', 'Basic reports']
    },
    {
      name: 'Pro',
      price: '$99',
      setup: '$299',
      popular: true,
      description: 'Most popular for growing businesses',
      limits: ['100 active loans', '200 properties'],
      features: ['Everything in Starter', 'eBay integration', 'Tax escrow tracking', 'Multi-user (3 seats)', 'SMS notifications', 'Auction calendar']
    },
    {
      name: 'Enterprise',
      price: '$199',
      setup: '$999',
      description: 'For high-volume operations',
      limits: ['Unlimited loans', 'Unlimited properties'],
      features: ['Everything in Pro', 'Full white-label', 'API access', 'Bulk import', 'Custom email templates', 'Priority support']
    }
  ];

  const renderComparisonValue = (value) => {
    if (value === true) {
      return <span className="comparison-check">✓</span>;
    }
    if (value === false) {
      return <span className="comparison-x">✗</span>;
    }
    return <span className="comparison-partial">{value}</span>;
  };

  return (
    <div className="marketing-features">
      {/* Hero Section */}
      <section className="marketing-hero marketing-hero-short">
        <div className="marketing-hero-content">
          <h1 className="marketing-hero-title">
            Everything You Need to Run Your Land Financing Business
          </h1>
          <p className="marketing-hero-subtitle">
            Website, listings, payments, contracts, customer portal, and loan management. All in one place.
          </p>
          <div className="marketing-hero-buttons">
            <Link to="/signup" className="btn btn-primary btn-large">
              Start Free Trial
            </Link>
            <Link to="/pricing" className="btn btn-secondary btn-large">
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Feature Cards Grid */}
      <section className="marketing-features-grid-section">
        <div className="container">
          <h2 className="section-title">Core Features</h2>
          <p className="section-subtitle">Purpose-built for land investors and contract-for-deed financing</p>

          <div className="marketing-features-grid">
            {featureCards.map((card, index) => (
              <div key={index} className="marketing-feature-card">
                <div className="feature-card-icon">{card.icon}</div>
                <h3 className="feature-card-title">{card.title}</h3>
                <p className="feature-card-description">{card.description}</p>
                <ul className="feature-card-list">
                  {card.highlights.map((item, i) => (
                    <li key={i}>
                      <span className="feature-check-icon">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="marketing-comparison-section">
        <div className="container">
          <h2 className="section-title">More Than Just Loan Servicing</h2>
          <p className="section-subtitle">
            See how DeedStack™ compares to spreadsheets and loan-only tools
          </p>

          {/* Desktop: Table view */}
          <div className="comparison-table-wrapper desktop-only">
            <table className="comparison-table competitor-comparison">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th className="competitor-col">
                    <div className="competitor-header">
                      <span className="competitor-icon">📊</span>
                      <span>Spreadsheets</span>
                    </div>
                  </th>
                  <th className="competitor-col">
                    <div className="competitor-header">
                      <span className="competitor-icon">💰</span>
                      <span>Loan-Only Tools</span>
                    </div>
                  </th>
                  <th className="deedstack-col">
                    <div className="competitor-header deedstack-header">
                      <span className="competitor-icon">🏠</span>
                      <span>DeedStack™</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((row, index) => (
                  <tr key={index}>
                    <td className="feature-name">{row.feature}</td>
                    <td className="competitor-col">{renderComparisonValue(row.spreadsheets)}</td>
                    <td className="competitor-col">{renderComparisonValue(row.loanOnly)}</td>
                    <td className="deedstack-col">{renderComparisonValue(row.deedstack)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: Stacked cards view */}
          <div className="comparison-mobile-cards mobile-only">
            <div className="comparison-mobile-card">
              <h4>📊 Spreadsheets</h4>
              <ul>
                {comparisonFeatures.map((row, index) => (
                  <li key={index}>
                    {renderComparisonValue(row.spreadsheets)}
                    <span>{row.feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="comparison-mobile-card">
              <h4>💰 Loan-Only Tools</h4>
              <ul>
                {comparisonFeatures.map((row, index) => (
                  <li key={index}>
                    {renderComparisonValue(row.loanOnly)}
                    <span>{row.feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="comparison-mobile-card highlight">
              <h4>🏠 DeedStack™</h4>
              <ul>
                {comparisonFeatures.map((row, index) => (
                  <li key={index}>
                    {renderComparisonValue(row.deedstack)}
                    <span>{row.feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="comparison-summary">
            <p>
              <strong>The difference:</strong> DeedStack™ is the only platform that combines a professional
              property website, customer portal, and loan management into one integrated solution.
            </p>
          </div>
        </div>
      </section>

      {/* Features by Tier */}
      <section className="marketing-tiers-section">
        <div className="container">
          <h2 className="section-title">Features by Plan</h2>
          <p className="section-subtitle">Choose the plan that fits your business</p>

          <div className="tiers-grid">
            {tiers.map((tier, index) => (
              <div key={index} className={`tier-card ${tier.popular ? 'tier-popular' : ''}`}>
                {tier.popular && <div className="tier-popular-badge">Most Popular</div>}
                <h3 className="tier-name">{tier.name}</h3>
                <div className="tier-pricing">
                  <span className="tier-price">{tier.price}</span>
                  <span className="tier-period">/month</span>
                </div>
                {tier.setup ? (
                  <div className="tier-setup">+ {tier.setup} setup</div>
                ) : (
                  <div className="tier-setup tier-no-setup">No setup fee</div>
                )}
                <p className="tier-description">{tier.description}</p>

                <div className="tier-limits">
                  {tier.limits.map((limit, i) => (
                    <div key={i} className="tier-limit">{limit}</div>
                  ))}
                </div>

                <ul className="tier-features">
                  {tier.features.map((feature, i) => (
                    <li key={i}>
                      <span className="tier-check">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>

                <Link
                  to="/signup"
                  className={`btn ${tier.popular ? 'btn-primary' : 'btn-secondary'} tier-cta`}
                >
                  Get Started
                </Link>
              </div>
            ))}
          </div>

          <div className="tiers-note">
            <p>All plans include a 14-day free trial. No credit card required to start.</p>
            <Link to="/pricing" className="tiers-link">View full pricing details →</Link>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="marketing-security-section">
        <div className="container">
          <h2 className="section-title">Enterprise-Grade Security</h2>
          <p className="section-subtitle">Your data and your customers' data is protected</p>

          <div className="security-grid">
            <div className="security-card">
              <div className="security-icon">🔒</div>
              <h3>Bank-Level Encryption</h3>
              <p>All data is encrypted in transit and at rest using AES-256 encryption.</p>
            </div>
            <div className="security-card">
              <div className="security-icon">💳</div>
              <h3>PCI Compliant</h3>
              <p>Payment processing through Square and Stripe meets PCI-DSS standards.</p>
            </div>
            <div className="security-card">
              <div className="security-icon">☁️</div>
              <h3>Cloud Hosted</h3>
              <p>Hosted on enterprise cloud infrastructure with automatic backups and 99.9% uptime.</p>
            </div>
            <div className="security-card">
              <div className="security-icon">🔐</div>
              <h3>Secure Authentication</h3>
              <p>Industry-standard authentication with encrypted passwords and session management.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta marketing-cta marketing-final-cta">
        <div className="cta-content">
          <h2>Ready to Professionalize Your Land Business?</h2>
          <p className="cta-subtitle">
            See DeedStack™ in action with our live demo, or start your free trial today.
          </p>

          <div className="demo-credentials">
            <p className="demo-label">Try our demo:</p>
            <div className="demo-info">
              <div className="demo-item">
                <span className="demo-icon">🌐</span>
                <a href="https://demo.deedstack.com/admin" target="_blank" rel="noopener noreferrer">
                  demo.deedstack.com/admin
                </a>
              </div>
              <div className="demo-item">
                <span className="demo-icon">📧</span>
                <span>demo@deedstack.com</span>
              </div>
              <div className="demo-item">
                <span className="demo-icon">🔑</span>
                <span>demodemo123</span>
              </div>
            </div>
          </div>

          <div className="marketing-cta-buttons">
            <Link to="/signup" className="btn btn-large btn-cta-primary">
              Start Free Trial
            </Link>
            <a
              href="https://demo.deedstack.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-large btn-cta-secondary"
            >
              View Live Demo
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default MarketingFeatures;
